import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, FormItem, Button, Typogaphy } from 'vnetwork-ui'
import {
    bulkUpsertActivityTargets,
    getActivityTargetsByEmployeeId,
    bulkUpsertSalesTargets,
    getSalesTargetsByEmployeeId,
} from '@/modules/Report/services'
import {
    UpsertActivityTargetPayload,
    UpsertSalesTargetPayload,
} from '@/modules/Report/types'
import { useEffect, useMemo } from 'react'
import { ModalProps } from '@/types/shared'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import ActivityTargetForm, {
    MonthlyTarget,
    createEmptyMonthsData,
} from '../components/ActivityTargetForm'
import toast from 'react-hot-toast'

export interface EditActivityTargetModalProps extends ModalProps {
    editRow: {
        employee_id: number
        employee_name: string
    }
    defaultYear?: number
    gte?: string
    lte?: string
}

interface FormData {
    employee_id: number | null
    target_year: number
    months: MonthlyTarget[]
    quickFill: {
        presentation: number
        poc: number
        contract: number
        revenue: number
    }
}

const EditActivityTargetModal = ({
    open,
    onClose,
    editRow,
    defaultYear,
    gte,
    lte,
}: EditActivityTargetModalProps) => {
    const { t } = useTranslation('report')
    const currentYear = defaultYear || new Date().getFullYear()

    const { control, handleSubmit, reset, setValue, register, getValues } =
        useForm<FormData>({
            defaultValues: {
                employee_id: editRow?.employee_id || null,
                target_year: currentYear,
                months: createEmptyMonthsData(),
                quickFill: { presentation: 0, poc: 0, contract: 0, revenue: 0 },
            },
        })

    const { data: existingData, isLoading: isLoadingTargets } =
        useQueryWithHandler({
            queryFn: () =>
                getActivityTargetsByEmployeeId(
                    editRow!.employee_id,
                    gte!,
                    lte!
                ),
            queryKey: [
                QUERY_KEYS.ACTIVITY_TARGETS,
                'employee',
                editRow?.employee_id,
                gte,
                lte,
            ],
            enabled: open && !!editRow?.employee_id && !!gte && !!lte,
        })

    // Fetch existing revenue targets
    const { data: revenueData } = useQueryWithHandler({
        queryFn: () =>
            getSalesTargetsByEmployeeId(editRow!.employee_id, gte!, lte!),
        queryKey: [
            QUERY_KEYS.ACTIVITY_TARGETS,
            'revenue',
            editRow?.employee_id,
            gte,
            lte,
        ],
        enabled: open && !!editRow?.employee_id && !!gte && !!lte,
    })
    // Reset form when modal opens or when existing data is loaded
    useEffect(() => {
        if (open) {
            let monthsData = createEmptyMonthsData()

            // If we have existing data from API, populate the form
            if (existingData?.activity_targets) {
                monthsData = monthsData.map((m) => {
                    const existing = existingData.activity_targets[m.month]
                    if (existing) {
                        return {
                            ...m,
                            presentation: existing.target_presentation || 0,
                            poc: existing.target_poc || 0,
                            contract: existing.target_contract || 0,
                        }
                    }
                    return m
                })
            }

            // Merge revenue targets
            if (revenueData?.revenue_targets) {
                monthsData = monthsData.map((m) => {
                    const existing = revenueData.revenue_targets[m.month]
                    if (existing) {
                        return {
                            ...m,
                            revenue: existing.target_amount || 0,
                        }
                    }
                    return m
                })
            }

            reset({
                employee_id: editRow?.employee_id || null,
                target_year: defaultYear || new Date().getFullYear(),
                months: monthsData,
                quickFill: { presentation: 0, poc: 0, contract: 0, revenue: 0 },
            })
        }
    }, [open, editRow, defaultYear, existingData, revenueData, reset])

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: bulkUpsertActivityTargets,
            onMutate: () => ({}),
        },
        {
            reactQueryKeys: [
                QUERY_KEYS.EMPLOYEE_DASHBOARD,
                QUERY_KEYS.ACTIVITY_TARGETS,
            ],
            onSuccess() {
                toast.success(
                    t('target_analytics.activityTarget.successMessage')
                )
            },
            onError() {
                toast.error(t('target_analytics.activityTarget.errorMessage'))
            },
        }
    )

    const { mutateAsync: mutateSalesAsync } = useMutationWithHandler(
        {
            mutationFn: bulkUpsertSalesTargets,
            onMutate: () => ({}),
        },
        {
            reactQueryKeys: [
                QUERY_KEYS.EMPLOYEE_DASHBOARD,
                QUERY_KEYS.ACTIVITY_TARGETS,
            ],
            onSuccess() {
                toast.success(t('target_analytics.saleTarget.successMessage'))
            },
            onError() {
                toast.error(t('target_analytics.saleTarget.errorMessage'))
            },
        }
    )

    const onSubmit = async (data: FormData) => {
        if (!data.employee_id) return

        const targets: UpsertActivityTargetPayload[] = []
        const salesTargets: UpsertSalesTargetPayload[] = []

        data.months.forEach((monthData) => {
            if (monthData.presentation > 0) {
                targets.push({
                    employee_id: data.employee_id!,
                    activity_type: 'presentation',
                    target_quantity: monthData.presentation || 0,
                    target_scope: 'month',
                    target_year: data.target_year,
                    target_month: monthData.month,
                })
            }
            if (monthData.poc > 0) {
                targets.push({
                    employee_id: data.employee_id!,
                    activity_type: 'poc',
                    target_quantity: monthData.poc || 0,
                    target_scope: 'month',
                    target_year: data.target_year,
                    target_month: monthData.month,
                })
            }
            if (monthData.contract > 0) {
                targets.push({
                    employee_id: data.employee_id!,
                    activity_type: 'contract',
                    target_quantity: monthData.contract || 0,
                    target_scope: 'month',
                    target_year: data.target_year,
                    target_month: monthData.month,
                })
            }

            // Add revenue target (only if amount > 0)
            if (monthData.revenue && monthData.revenue > 0) {
                salesTargets.push({
                    employee_id: data.employee_id!,
                    target_amount: monthData.revenue,
                    target_scope: 'month',
                    target_year: data.target_year,
                    target_month: monthData.month,
                    is_active: true,
                })
            }
        })

        // Call APIs only if they have data
        const promises = []

        if (targets.length > 0) {
            promises.push(mutateAsync({ targets }))
        }

        if (salesTargets.length > 0) {
            promises.push(mutateSalesAsync({ targets: salesTargets }))
        }

        try {
            if (promises.length > 0) {
                await Promise.all(promises)
                reset()
                onClose()
            } else {
                // No targets to save, just close modal
                reset()
                onClose()
            }
        } catch (error) {
            console.error('Failed to save targets', error)
        }
    }

    const YEAR_OPTIONS = useMemo(
        () =>
            Array.from({ length: 10 }, (_, i) => ({
                label: `${currentYear - 5 + i}`,
                value: currentYear - 5 + i,
            })),
        [currentYear]
    )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={`${t('edit')} - ${editRow?.employee_name}`}
            maxWidth="md"
        >
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-4"
            >
                {/* Header Row */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="border rounded-lg p-2 bg-muted/30">
                        <Typogaphy className="font-medium">
                            {t('employee')}: {editRow?.employee_name}
                        </Typogaphy>
                    </div>
                    <FormItem
                        control={control}
                        name="target_year"
                        type="select"
                        label={t('year')}
                        options={YEAR_OPTIONS}
                    />
                </div>

                {/* Shared Form Component */}
                <ActivityTargetForm
                    control={control}
                    register={register}
                    setValue={setValue}
                    getValues={getValues}
                    isLoading={isLoadingTargets}
                />

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={onClose}
                        disabled={isPending}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        loading={isPending}
                        disabled={isPending || isLoadingTargets}
                    >
                        {t('save')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default EditActivityTargetModal
